import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { MegaphoneIcon } from '@heroicons/react/24/outline';
import { Button as ButtonV2 } from '@restworld/ui-ds-base';

import { cn } from '@restworld/utils-common';

import { DashboardFeedbackDialog } from './dashboard-feedback-dialog';

/**
 * Entry point of the open-feedback survey: the button opens our own dialog
 * (`DashboardFeedbackDialog`), which stores the answer in our backend via
 * `useSurvey`. It used to open the PostHog survey popover (`displaySurvey`),
 * which only worked for employers with Measurement cookie consent (~75%) and
 * needed a MutationObserver to notice the popover closing.
 */
function DashboardFeedbackCard() {
  const { t } = useTranslation('dashboard-page');
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  return (
    <>
      <CardContainer>
        <TextWrapper>
          <CardTitle>{t('your_opinion_matters')}</CardTitle>
          <CardSubtitle>{t('tell_us_how_to_improve')}</CardSubtitle>
        </TextWrapper>
        <ButtonV2 variant="tertiary" StartIcon={MegaphoneIcon} fullwidth onClick={() => setIsDialogOpen(true)}>
          {t('leave_feedback')}
        </ButtonV2>
      </CardContainer>
      {isDialogOpen && <DashboardFeedbackDialog isOpen={isDialogOpen} onClose={() => setIsDialogOpen(false)} />}
    </>
  );
}

export default DashboardFeedbackCard;

const CardContainer = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'flex h-full min-h-[200px] flex-col justify-between rounded-2xl bg-neutral-10 pt-10 px-5 pb-5 border border-neutral-40 shadow-[0px_2px_4px_0px_rgba(0,0,26,0.05)]',
      className
    )}
    {...props}
  />
);
const TextWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-1', className)} {...props} />
);
const CardTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-base font-semibold text-neutral-700', className)} {...props} />
);
const CardSubtitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <span className={cn('text-base font-normal text-neutral-100', className)} {...props} />
);
